/**
 * IANA time zones in plain words (epic PI-SPEC-SW-E294, feature
 * PI-SPEC-SW-1511, PKG-PM-F frame F1, UX-PM-15): nobody types a zone id.
 *
 * - {@link allTimeZones}: every zone the runtime knows, or a short fallback
 *   list where `Intl.supportedValuesOf` is missing (Settings and the Meeting
 *   schedule tab both list these).
 * - {@link zoneLabel}: "Central Time — Chicago", "India — Kolkata".
 * - {@link utcOffsetLabel}: "UTC−5", "UTC+5:30", "UTC".
 * - {@link detectedTimeZone}: this computer's zone.
 *
 * Intl only, no date library. Labels and offsets are cached per zone (and
 * per instant for offsets), since a list renders a few hundred of them.
 */

/** Zones listed where the runtime cannot list its own (`Intl.supportedValuesOf` missing). */
export const FALLBACK_TIME_ZONES: readonly string[] = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Kolkata",
  "Australia/Sydney",
];

type IntlWithSupportedValues = typeof Intl & { supportedValuesOf?: (key: string) => string[] };

/** Every IANA zone the runtime knows (`Intl.supportedValuesOf("timeZone")`), else {@link FALLBACK_TIME_ZONES}. */
export function allTimeZones(): string[] {
  const supportedValuesOf = (Intl as IntlWithSupportedValues).supportedValuesOf;
  if (typeof supportedValuesOf === "function") {
    try {
      return supportedValuesOf("timeZone");
    } catch {
      // Fall through to the short list.
    }
  }
  return [...FALLBACK_TIME_ZONES];
}

/**
 * Old IANA names some runtimes still list (V8 and Node list ICU's
 * `Asia/Calcutta`, not `Asia/Kolkata`) mapped to today's names, so a search
 * for "Kolkata" or "Kyiv" finds the zone and a list never shows it twice.
 */
const CURRENT_ZONE_IDS: Readonly<Record<string, string>> = {
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Catamarca": "America/Argentina/Catamarca",
  "America/Cordoba": "America/Argentina/Cordoba",
  "America/Jujuy": "America/Argentina/Jujuy",
  "America/Mendoza": "America/Argentina/Mendoza",
  "America/Indianapolis": "America/Indiana/Indianapolis",
  "America/Louisville": "America/Kentucky/Louisville",
  "America/Godthab": "America/Nuuk",
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Atlantic/Faeroe": "Atlantic/Faroe",
  "Europe/Kiev": "Europe/Kyiv",
  "Pacific/Enderbury": "Pacific/Kanton",
  "Pacific/Ponape": "Pacific/Pohnpei",
  "Pacific/Truk": "Pacific/Chuuk",
};

/** Today's IANA name of `id` when the runtime knows it (`Asia/Calcutta` → `Asia/Kolkata`), else `id`. */
export function currentZoneId(id: string): string {
  const current = CURRENT_ZONE_IDS[id];
  return current && isKnownTimeZone(current) ? current : id;
}

/** The old name of a zone a runtime may list instead (`Asia/Kolkata` → `Asia/Calcutta`), for search; null when none. */
export function formerZoneId(id: string): string | null {
  return Object.entries(CURRENT_ZONE_IDS).find(([, current]) => current === id)?.[0] ?? null;
}

/**
 * {@link allTimeZones} under today's names, without duplicates: the zones a
 * person picks from. `UTC` is added because `Intl.supportedValuesOf` leaves it
 * out while the meeting pattern accepts it.
 */
export function pickableTimeZones(): string[] {
  return [...new Set([...allTimeZones().map(currentZoneId), "UTC"])];
}

/** This computer's IANA zone, or "UTC" when the runtime does not say. */
export function detectedTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** True when the runtime knows `timeZone`. */
export function isKnownTimeZone(timeZone: string): boolean {
  if (timeZone.trim().length === 0) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

const UTC_IDS = new Set(["UTC", "Etc/UTC", "Etc/GMT", "GMT", "Etc/Universal", "Etc/Zulu", "Universal", "Zulu"]);

/** The city of a zone id: `America/Argentina/Buenos_Aires` → "Buenos Aires", `UTC` → "UTC". */
export function zoneCity(id: string): string {
  const last = id.split("/").pop() ?? id;
  return last.replace(/_/g, " ");
}

const nameCache = new Map<string, string | null>();

/**
 * The zone's long generic name with "Standard Time" said as "Time", and
 * without "Time" when more than one word is left before it: "Central Time"
 * (Chicago and Mexico City alike), "Central European", "India Time",
 * "United Kingdom". Null when the runtime has no name for it (it then says
 * "GMT+5:30" or the like) or does not know the zone.
 */
export function zoneName(id: string): string | null {
  const cached = nameCache.get(id);
  if (cached !== undefined) return cached;
  let name: string | null = null;
  if (UTC_IDS.has(id)) {
    name = "UTC";
  } else {
    try {
      const part = new Intl.DateTimeFormat("en-US", { timeZone: id, timeZoneName: "longGeneric" })
        .formatToParts(new Date(Date.UTC(2026, 0, 15)))
        .find((p) => p.type === "timeZoneName")?.value;
      if (part && !/^(GMT|UTC)([+\-\u2212]|$)/.test(part)) {
        const generic = part.replace(/ Standard Time$/, " Time");
        name = /\S+ \S+ Time$/.test(generic) ? generic.replace(/ Time$/, "") : generic;
      }
    } catch {
      name = null;
    }
  }
  nameCache.set(id, name);
  return name;
}

/**
 * A zone in plain words: "Central Time — Chicago", "Central European —
 * Berlin", "India — Kolkata"; the city alone when the runtime has no name for
 * the zone (or the name is the city); "UTC" for UTC.
 */
export function zoneLabel(id: string): string {
  if (UTC_IDS.has(id)) return "UTC";
  const city = zoneCity(id);
  const name = zoneName(id);
  if (!name || name === city) return city;
  return `${name} — ${city}`;
}

const wallClockFormats = new Map<string, Intl.DateTimeFormat>();

/** A cached 24-hour wall-clock formatter for one zone. @throws RangeError for an unknown zone */
function wallClockFormat(id: string): Intl.DateTimeFormat {
  let format = wallClockFormats.get(id);
  if (!format) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone: id,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
    wallClockFormats.set(id, format);
  }
  return format;
}

/** Minutes the zone's wall clock is ahead of UTC at `at` (Chicago in summer: −300). Null for an unknown zone. */
export function utcOffsetMinutes(id: string, at: Date = new Date()): number | null {
  try {
    const parts = wallClockFormat(id).formatToParts(at);
    const part = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value);
    const wallAsUtc = Date.UTC(part("year"), part("month") - 1, part("day"), part("hour"), part("minute"));
    const instant = at.getTime() - (((at.getTime() % 60_000) + 60_000) % 60_000);
    return Math.round((wallAsUtc - instant) / 60_000);
  } catch {
    return null;
  }
}

/** "UTC−5", "UTC+5:30", "UTC" for a zero offset (a true minus sign, U+2212). Empty for an unknown zone. */
export function utcOffsetLabel(id: string, at: Date = new Date()): string {
  const minutes = utcOffsetMinutes(id, at);
  if (minutes === null) return "";
  if (minutes === 0) return "UTC";
  const sign = minutes < 0 ? "−" : "+";
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const rest = abs % 60;
  return `UTC${sign}${String(hours)}${rest ? `:${String(rest).padStart(2, "0")}` : ""}`;
}

/** True when the zone's offset differs between January and July of `at`'s year (it changes its clocks). */
export function changesClocks(id: string, at: Date = new Date()): boolean {
  const year = at.getUTCFullYear();
  const january = utcOffsetMinutes(id, new Date(Date.UTC(year, 0, 15)));
  const july = utcOffsetMinutes(id, new Date(Date.UTC(year, 6, 15)));
  return january !== null && july !== null && january !== july;
}
